import { api } from "@dreambigger/shared/src/api/acquire";
import { useAuthToken } from "../passwordless-auth";

// All the paths for this api domain.
const basePath = "/repay";
const repayPaths = {
  getCheckoutForm: `${basePath}/checkout-form`,
  createOneTimeUseUrl: `${basePath}/one-time-use-url`,
};

type GetCheckoutFormParams = {
  repayConfigurationUUID: string;
  onboardingApplicationId: string;
};

type OneTimeUrlParams = {
  amount?: string;
  transaction_type?: "auth" | "sale" | "scheduled_sale";
  Source?: string;
  [key: string]: string | undefined;
};

type CreateOneTimeUseUrlParams = {
  repayConfigurationUUID: string;
  onboardingApplicationId: string;
  checkoutFormId: string;
  oneTimeUrlParams?: OneTimeUrlParams;
};

type RepayPayment = {
  uuid: string;
  paymentReference: string;
  customerId: string;
  amount: string;
  convenienceFee: string;
  transactionType?: string;
  gatewayMid?: string;
  resultCode?: string;
  resultText?: string;
  paymentDate?: string;
  createdAt: string;
  updatedAt: string;
};

export default function useRepay(financialInstitutionId: string) {
  const tokenHelper = useAuthToken(financialInstitutionId);

  // Create a one-time use URL for Repay payment
  // POST /repay/one-time-use-url
  const createOneTimeUseUrl = async (
    params: CreateOneTimeUseUrlParams
  ): Promise<{
    url: string;
  }> => {
    const token = tokenHelper.getJwtToken();

    const { data } = await api.post<{
      url: string;
    }>(repayPaths.createOneTimeUseUrl, params, {
      headers: {
        Authorization: tokenHelper.formatAuthHeader(token),
      },
    });

    return data;
  };

  // Get a checkout form for Repay
  // POST /repay/checkout-form
  const getCheckoutForm = async (
    params: GetCheckoutFormParams
  ): Promise<{
    checkoutFormId: string;
  }> => {
    const token = tokenHelper.getJwtToken();

    const { data } = await api.post<{
      checkoutFormId: string;
    }>(repayPaths.getCheckoutForm, params, {
      headers: {
        Authorization: tokenHelper.formatAuthHeader(token),
      },
    });

    return data;
  };

  // Get repay payment for an application
  // GET /onboarding-applications/:id/repay-payment
  const getRepayPayment = async (
    applicationId: string
  ): Promise<RepayPayment> => {
    const token = tokenHelper.getJwtToken();

    const { data } = await api.get<RepayPayment>(
      `/onboarding-applications/${applicationId}/repay-payment`,
      {
        headers: {
          Authorization: tokenHelper.formatAuthHeader(token),
        },
      }
    );

    return data;
  };

  return {
    createOneTimeUseUrl,
    getCheckoutForm,
    getRepayPayment,
  };
}
